import React, { useState, useEffect } from 'react';
import { webhookAPI } from '../../services/api';
import '../Customers/Customers.css';
import './Admin.css';

function WebhookManagement() {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resettingId, setResettingId] = useState(null);

  useEffect(() => {
    loadWebhooks();
  }, []);

  const loadWebhooks = async () => {
    try {
      setLoading(true);
      const response = await webhookAPI.getAll();
      setWebhooks(response.webhooks || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load webhooks');
      console.error('Error loading webhooks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResetWebhook = async (id) => {
    if (!window.confirm('Are you sure you want to reset this webhook for re-processing?')) {
      return;
    }

    try {
      setResettingId(id);
      setError('');
      setSuccess('');
      await webhookAPI.reset(id);
      setSuccess('Webhook reset successfully and queued for re-processing');
      loadWebhooks();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset webhook');
      console.error('Error resetting webhook:', err);
    } finally {
      setResettingId(null);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending':
        return 'badge-warning';
      case 'processing':
        return 'badge-info';
      case 'processed':
        return 'badge-success';
      case 'failed':
        return 'badge-error';
      default:
        return 'badge-default';
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-NZ', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch (e) {
      return dateString;
    }
  };

  if (loading && webhooks.length === 0) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-main-wrapper">
          <main className="dashboard-content">
            <div className="customer-list-container">
              <div className="loading">Loading webhooks...</div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-main-wrapper">
        <main className="dashboard-content">
          <div className="customer-list-container">
            <div className="customer-list-header">
              <h1>Shopify Webhooks</h1>
              <p className="subtitle">Manage and monitor Shopify webhook processing</p>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {success && (
              <div className="success-message">
                {success}
              </div>
            )}

            <div className="logs-stats">
              <span>Showing {webhooks.length} webhooks</span>
              {loading && <span className="loading-indicator">Refreshing...</span>}
              <button onClick={loadWebhooks} className="btn btn-secondary btn-small">
                Refresh
              </button>
            </div>

            <div className="logs-container">
              {webhooks.length === 0 ? (
                <div className="info-box">
                  <p>No webhooks found.</p>
                </div>
              ) : (
                <div className="webhooks-list">
                  {webhooks.map((webhook) => (
                    <div key={webhook.id} className="log-entry">
                      <div className="log-header">
                        <div className="log-badges">
                          <span className={`badge ${getStatusBadgeClass(webhook.status)}`}>
                            {webhook.status.toUpperCase()}
                          </span>
                          <span className="badge badge-default">
                            {webhook.topic}
                          </span>
                        </div>
                        <div className="log-timestamp">
                          {formatDate(webhook.created_at)}
                        </div>
                      </div>
                      <div className="log-message">
                        <strong>Webhook ID:</strong> {webhook.id} | 
                        <strong> Shopify ID:</strong> {webhook.shopify_webhook_id}
                        {webhook.customer_order_id && (
                          <> | <strong>Order ID:</strong> {webhook.customer_order_id}</>
                        )}
                      </div>
                      {webhook.error_message && (
                        <div className="log-context">
                          <div className="log-context-item">
                            <span className="log-context-key">Error:</span>
                            <span className="log-context-value">{webhook.error_message}</span>
                          </div>
                        </div>
                      )}
                      <div className="webhook-actions">
                        {(webhook.status === 'failed' || webhook.status === 'processed') && (
                          <button
                            onClick={() => handleResetWebhook(webhook.id)}
                            disabled={resettingId === webhook.id}
                            className="btn btn-primary btn-small"
                          >
                            {resettingId === webhook.id ? 'Resetting...' : 'Reset & Retry'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default WebhookManagement;